'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@core/utils';
import { usePathname, useSearchParams } from 'next/navigation';
import { useCallback, useRef, useState } from 'react';
import { SearchTopAdBanner } from '@/app/_components/ads';
import { usePageSearchBarVisibility, useSearchHistoryStore } from '@/app/_components/SearchBar';
import { DEFAULT_DONATION_SEARCH_CATEGORY, DEFAULT_SEARCH_CATEGORY, isDonationSearchCategory, isSearchCategory, SEARCH_CATEGORY_QUERY_KEY, type SearchCategory, type SearchScope } from '@/app/_components/SearchBar/constants';
import { shouldShowMobileTrendKeyword } from '@/app/_components/TrendKeyword/constants';
import { TrendKeywordBar } from '@/app/_components/TrendKeyword/TrendKeywordBar';
import { useTrendKeywords } from '@/app/_components/TrendKeyword/useTrendKeywords';
import { BREAKPOINTS } from '@/constants';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useAppBack } from '@/modules/wello-app/_hooks/useAppBack';
import { GTM_EVENT_TYPE, trackGtmEvent } from '@/utils/analytics/gtm';
import { AllSearch } from './_components/AllSearch';
import { DonationBusinessSearch } from './_components/DonationBusinessSearch';
import { FestivalSearch } from './_components/FestivalSearch/FestivalSearch';
import { GiftSearch } from './_components/GiftSearch';
import { HometownNewsSearch } from './_components/HometownNewsSearch';
import { LocalMarketSearch } from './_components/LocalMarketSearch';
import { PolicyInfoSearch } from './_components/PolicyInfoSearch';
import { PolicySearch } from './_components/PolicySearch';
import { SearchPageHeader } from './_components/SearchPageHeader';
import { useIntegratedSearchFilter } from './_hooks/useIntegratedSearchFilter';
import { usePolicyFilter } from './_hooks/usePolicyFilter';
import { UrlFilterControllerProvider, useCategoryFilterCount } from './_hooks/useUrlFilterController';
import { SECTION_HEADER_MAX_W } from './constants';
const resolveCategory = (categoryParam: string | null, scope?: SearchScope): SearchCategory => {
  if (scope === 'donation') {
    return isDonationSearchCategory(categoryParam) ? categoryParam : DEFAULT_DONATION_SEARCH_CATEGORY;
  }
  return isSearchCategory(categoryParam) ? categoryParam : DEFAULT_SEARCH_CATEGORY;
};
export function SearchPageView(t0) {
  const $ = _c(2);
  const {
    scope
  } = t0;
  let t1;
  if ($[0] !== scope) {
    t1 = <UrlFilterControllerProvider><SearchPageContent scope={scope} /></UrlFilterControllerProvider>;
    $[0] = scope;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function SearchPageContent(t0) {
  const $ = _c(84);
  const {
    scope
  } = t0;
  useAppBack();
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  const searchParams = useSearchParams();
  const resultsSectionRef = useRef(null);
  const {
    state,
    setKeyword,
    toggleTargetCode,
    toggleProvisionCode,
    toggleJobCode,
    toggleAgeCode,
    toggleIncludeExpired,
    resetFilters,
    setSort,
    setPage,
    activeFilterCount
  } = usePolicyFilter();
  const {
    state: integratedState,
    setRegionCode,
    setSubRegionCode,
    toggleGiftCategory,
    toggleGiftInstitution,
    toggleDonationInstitution,
    setDonationSort,
    setFestivalLclsSystm2,
    setFestivalRegionCode,
    setFestivalSubRegionCode,
    setFestivalRadius,
    setFestivalMonth,
    resetHometownNewsFilters,
    resetGiftFilters,
    resetDonationFilters,
    resetFestivalFilters,
    hometownNewsFilterCount,
    giftFilterCount,
    donationFilterCount,
    festivalFilterCount
  } = useIntegratedSearchFilter();
  const policyInfoFilterCount = useCategoryFilterCount("policy-info");
  const searchHistory = useSearchHistoryStore(_temp);
  const add = useSearchHistoryStore(_temp2);
  const remove = useSearchHistoryStore(_temp3);
  const clear = useSearchHistoryStore(_temp4);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const {
    ref: searchBarRef
  } = usePageSearchBarVisibility();
  const pathname = usePathname() ?? "";
  const {
    keywords: trendKeywords,
    isPending: isTrendPending
  } = useTrendKeywords();
  let t1;
  if ($[0] !== isTrendPending || $[1] !== pathname || $[2] !== trendKeywords) {
    t1 = shouldShowMobileTrendKeyword(pathname) && (isTrendPending || trendKeywords.length > 0);
    $[0] = isTrendPending;
    $[1] = pathname;
    $[2] = trendKeywords;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const isTrendKeywordVisible = t1;
  let t2;
  if ($[4] !== scope || $[5] !== searchParams) {
    const categoryParam = searchParams.get(SEARCH_CATEGORY_QUERY_KEY);
    t2 = resolveCategory(categoryParam, scope);
    $[4] = scope;
    $[5] = searchParams;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const category = t2;
  const isAllCategory = category === "all";
  const isPolicyCategory = category === "policy";
  let t3;
  if ($[7] !== add || $[8] !== category || $[9] !== setKeyword) {
    t3 = keyword => {
      setKeyword(keyword);
      add(keyword);
      if (keyword && category !== "all") {
        trackGtmEvent(GTM_EVENT_TYPE.SEARCH, {
          search_type: "\uD1B5\uD569\uAC80\uC0C9",
          search_term: keyword,
          search_category: category
        });
      }
    };
    $[7] = add;
    $[8] = category;
    $[9] = setKeyword;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const handleSearch = t3;
  let t4;
  if ($[11] !== category || $[12] !== donationFilterCount || $[13] !== festivalFilterCount || $[14] !== giftFilterCount || $[15] !== hometownNewsFilterCount || $[16] !== policyInfoFilterCount) {
    const getIntegratedFilterCount = function getIntegratedFilterCount() {
      switch (category) {
        case "hometown-news":
          {
            return hometownNewsFilterCount;
          }
        case "policy-info":
          {
            return policyInfoFilterCount;
          }
        case "gifts":
          {
            return giftFilterCount;
          }
        case "hometown-business":
          {
            return donationFilterCount;
          }
        case "festival":
          {
            return festivalFilterCount;
          }
        default:
          {
            return 0;
          }
      }
    };
    t4 = getIntegratedFilterCount();
    $[11] = category;
    $[12] = donationFilterCount;
    $[13] = festivalFilterCount;
    $[14] = giftFilterCount;
    $[15] = hometownNewsFilterCount;
    $[16] = policyInfoFilterCount;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const integratedFilterCount = t4;
  const hasIntegratedFilter = category === "hometown-news" || category === "policy-info" || category === "gifts" || category === "hometown-business" || category === "festival";
  const filterCount = isAllCategory ? 0 : isPolicyCategory ? activeFilterCount : integratedFilterCount;
  let t5;
  if ($[18] !== hasIntegratedFilter || $[19] !== isAllCategory || $[20] !== isPolicyCategory) {
    t5 = !isAllCategory && (isPolicyCategory || hasIntegratedFilter) ? () => setIsFilterOpen(true) : undefined;
    $[18] = hasIntegratedFilter;
    $[19] = isAllCategory;
    $[20] = isPolicyCategory;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  const onFilterClick = t5;
  let t6;
  if ($[22] !== isDesktopViewport) {
    t6 = () => {
      const resultsSection = resultsSectionRef.current;
      if (!resultsSection) {
        return;
      }
      const top = resultsSection.getBoundingClientRect().top + window.scrollY - (isDesktopViewport ? 24 : 56);
      window.scrollTo({
        top: Math.max(top, 0),
        behavior: "auto"
      });
    };
    $[22] = isDesktopViewport;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const scrollToResultsTop = t6;
  let t7;
  if ($[24] !== scrollToResultsTop || $[25] !== setPage) {
    t7 = page => {
      setPage(page);
      scrollToResultsTop();
    };
    $[24] = scrollToResultsTop;
    $[25] = setPage;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  const handlePageChange = t7;
  let t8;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => setIsFilterOpen(false);
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  const closeFilter = t8;
  let t9;
  if ($[28] !== category || $[29] !== handlePageChange || $[30] !== integratedState || $[31] !== isFilterOpen || $[32] !== resetDonationFilters || $[33] !== resetFestivalFilters || $[34] !== resetFilters || $[35] !== resetGiftFilters || $[36] !== resetHometownNewsFilters || $[37] !== setDonationSort || $[38] !== setFestivalLclsSystm2 || $[39] !== setFestivalMonth || $[40] !== setFestivalRadius || $[41] !== setFestivalRegionCode || $[42] !== setFestivalSubRegionCode || $[43] !== setRegionCode || $[44] !== setSort || $[45] !== setSubRegionCode || $[46] !== state || $[47] !== toggleAgeCode || $[48] !== toggleDonationInstitution || $[49] !== toggleGiftCategory || $[50] !== toggleGiftInstitution || $[51] !== toggleIncludeExpired || $[52] !== toggleJobCode || $[53] !== toggleProvisionCode || $[54] !== toggleTargetCode) {
    t9 = () => {
      switch (category) {
        case "all":
          {
            return <AllSearch keyword={state.keyword} />;
          }
        case "policy":
          {
            return <PolicySearch isFilterOpen={isFilterOpen} onCloseFilter={closeFilter} state={state} onToggleTargetCode={toggleTargetCode} onToggleProvisionCode={toggleProvisionCode} onToggleJobCode={toggleJobCode} onToggleAgeCode={toggleAgeCode} onToggleIncludeExpired={toggleIncludeExpired} onResetFilters={resetFilters} onSortChange={setSort} onPageChange={handlePageChange} />;
          }
        case "hometown-news":
          {
            return <HometownNewsSearch keyword={state.keyword} page={state.page} onPageChange={handlePageChange} isFilterOpen={isFilterOpen} onCloseFilter={closeFilter} regionCode={integratedState.regionCode} subRegionCode={integratedState.subRegionCode} onRegionChange={setRegionCode} onSubRegionChange={setSubRegionCode} onReset={resetHometownNewsFilters} />;
          }
        case "festival":
          {
            return <FestivalSearch keyword={state.keyword} page={state.page} onPageChange={handlePageChange} isFilterOpen={isFilterOpen} onCloseFilter={closeFilter} lclsSystm2={integratedState.festivalLclsSystm2} onSelectLclsSystm2={setFestivalLclsSystm2} regionCode={integratedState.festivalRegionCode} subRegionCode={integratedState.festivalSubRegionCode} onSelectRegion={setFestivalRegionCode} onSelectSubRegion={setFestivalSubRegionCode} radius={integratedState.festivalRadius} onSelectRadius={setFestivalRadius} month={integratedState.festivalMonth} onSelectMonth={setFestivalMonth} onReset={resetFestivalFilters} />;
          }
        case "policy-info":
          {
            return <PolicyInfoSearch keyword={state.keyword} page={state.page} onPageChange={handlePageChange} isFilterOpen={isFilterOpen} onCloseFilter={closeFilter} />;
          }
        case "gifts":
          {
            return <GiftSearch keyword={state.keyword} page={state.page} onPageChange={handlePageChange} isFilterOpen={isFilterOpen} onCloseFilter={closeFilter} categoryIds={integratedState.giftCategoryIds} institutionIds={integratedState.giftInstitutionIds} onToggleCategory={toggleGiftCategory} onToggleInstitution={toggleGiftInstitution} onReset={resetGiftFilters} />;
          }
        case "hometown-business":
          {
            return <DonationBusinessSearch keyword={state.keyword} page={state.page} onPageChange={handlePageChange} isFilterOpen={isFilterOpen} onCloseFilter={closeFilter} institutionIds={integratedState.donationInstitutionIds} sort={integratedState.donationSort} onToggleInstitution={toggleDonationInstitution} onSortChange={setDonationSort} onReset={resetDonationFilters} />;
          }
        case "welmarket":
          {
            return <LocalMarketSearch keyword={state.keyword} page={state.page} onPageChange={handlePageChange} />;
          }
      }
    };
    $[28] = category;
    $[29] = handlePageChange;
    $[30] = integratedState;
    $[31] = isFilterOpen;
    $[32] = resetDonationFilters;
    $[33] = resetFestivalFilters;
    $[34] = resetFilters;
    $[35] = resetGiftFilters;
    $[36] = resetHometownNewsFilters;
    $[37] = setDonationSort;
    $[38] = setFestivalLclsSystm2;
    $[39] = setFestivalMonth;
    $[40] = setFestivalRadius;
    $[41] = setFestivalRegionCode;
    $[42] = setFestivalSubRegionCode;
    $[43] = setRegionCode;
    $[44] = setSort;
    $[45] = setSubRegionCode;
    $[46] = state;
    $[47] = toggleAgeCode;
    $[48] = toggleDonationInstitution;
    $[49] = toggleGiftCategory;
    $[50] = toggleGiftInstitution;
    $[51] = toggleIncludeExpired;
    $[52] = toggleJobCode;
    $[53] = toggleProvisionCode;
    $[54] = toggleTargetCode;
    $[55] = t9;
  } else {
    t9 = $[55];
  }
  const renderSection = t9;
  const t10 = SECTION_HEADER_MAX_W[category];
  let t11;
  if ($[56] !== t10) {
    t11 = cn("w-full max-w-screen-xl", t10);
    $[56] = t10;
    $[57] = t11;
  } else {
    t11 = $[57];
  }
  let t12;
  if ($[58] !== searchHistory) {
    t12 = searchHistory ?? [];
    $[58] = searchHistory;
    $[59] = t12;
  } else {
    t12 = $[59];
  }
  let t13;
  if ($[60] !== isTrendKeywordVisible) {
    t13 = isTrendKeywordVisible ? <TrendKeywordBar /> : undefined;
    $[60] = isTrendKeywordVisible;
    $[61] = t13;
  } else {
    t13 = $[61];
  }
  let t14;
  if ($[62] !== clear || $[63] !== filterCount || $[64] !== handleSearch || $[65] !== onFilterClick || $[66] !== remove || $[67] !== scope || $[68] !== searchBarRef || $[69] !== state.keyword || $[70] !== t12 || $[71] !== t13) {
    t14 = <SearchPageHeader ref={searchBarRef} scope={scope} keyword={state.keyword} filterCount={filterCount} recentKeywords={t12} onSearch={handleSearch} onFilterClick={onFilterClick} onSelectRecent={handleSearch} onRemoveRecent={remove} onClearRecent={clear} trendKeyword={t13} />;
    $[62] = clear;
    $[63] = filterCount;
    $[64] = handleSearch;
    $[65] = onFilterClick;
    $[66] = remove;
    $[67] = scope;
    $[68] = searchBarRef;
    $[69] = state.keyword;
    $[70] = t12;
    $[71] = t13;
    $[72] = t14;
  } else {
    t14 = $[72];
  }
  let t15;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="mt-12 mobile-tablet:mt-0 mobile-tablet:px-16 mobile-tablet:py-12"><SearchTopAdBanner /></div>;
    $[73] = t15;
  } else {
    t15 = $[73];
  }
  let t16;
  if ($[74] !== t11 || $[75] !== t14) {
    t16 = <div className={t11}>{t14}{t15}</div>;
    $[74] = t11;
    $[75] = t14;
    $[76] = t16;
  } else {
    t16 = $[76];
  }
  let t17;
  if ($[77] !== renderSection) {
    t17 = renderSection();
    $[77] = renderSection;
    $[78] = t17;
  } else {
    t17 = $[78];
  }
  let t18;
  if ($[79] !== t17) {
    t18 = <div ref={resultsSectionRef} className="w-full">{t17}</div>;
    $[79] = t17;
    $[80] = t18;
  } else {
    t18 = $[80];
  }
  let t19;
  if ($[81] !== t16 || $[82] !== t18) {
    t19 = <div className="flex w-full flex-col items-center gap-12 p-40 mobile-tablet:gap-0 mobile-tablet:p-0">{t16}{t18}</div>;
    $[81] = t16;
    $[82] = t18;
    $[83] = t19;
  } else {
    t19 = $[83];
  }
  return t19;
}
function _temp4(state_3) {
  return state_3.clear;
}
function _temp3(state_2) {
  return state_2.remove;
}
function _temp2(state_1) {
  return state_1.add;
}
function _temp(state_0) {
  return state_0.data;
}